import dayjs from 'dayjs';
import type { WorldCupMatchCardData } from '@/components/predic/types/market';
import { formatDateTime } from '@/lib/utils/date';
import { WorldCupGameItem } from '../hooks/use-world-cup-games';
import type { MarketListEvent, MarketListMarket } from '@/hooks/use-market-list';
import { getTeamLogo } from '@/lib/world_cup_2026/common-data';

export function toWorldCupMatchCardData(game: WorldCupGameItem): WorldCupMatchCardData {
  const homeOdds = findTeamOdds(game, game.home_team.name);
  const awayOdds = findTeamOdds(game, game.away_team.name);
  const drawOdds = game.moneyline.find((odd) => {
    const lowerLabel = odd.group_item_title.toLowerCase();

    return (
      lowerLabel.includes('draw') &&
      lowerLabel !== game.home_team.name.toLowerCase() &&
      lowerLabel !== game.away_team.name.toLowerCase()
    );
  });

  return {
    market_event_id: String(game.polymarket_id),
    status_label: formatStatusLabel(game),
    volume: formatVolume(game.total_volume),
    draw_probability: formatProbability(drawOdds?.price),
    home_team: {
      name: game.home_team.name,
      probability: formatProbability(homeOdds?.price),
      probability_class_name: 'text-[#12B3CF]',
      image_src: game.home_team.logo ?? undefined,
      avatar_fallback: getInitials(game.home_team.name),
    },
    away_team: {
      name: game.away_team.name,
      probability: formatProbability(awayOdds?.price),
      probability_class_name: 'text-[#F5BA80]',
      image_src: game.away_team.logo ?? undefined,
      avatar_fallback: getInitials(game.away_team.name),
    },
    slug: game.slug,
  };
}

/**
 * 从 /api/market/markets 的 MarketListEvent（fifa-world-cup tag 的比赛事件）映射比赛卡片数据。
 * 约定：markets 中 group_item_title 为 'Draw' 的是平局盘，其余前两个依次为主/客队盘。
 */
export function toWorldCupMatchCardDataFromEvent(event: MarketListEvent): WorldCupMatchCardData {
  const isDrawMarket = (m: MarketListMarket) => m.group_item_title.toLowerCase().includes('draw');
  const drawMarket = event.markets.find(isDrawMarket);
  const [homeMarket, awayMarket] = event.markets.filter((m) => !isDrawMarket(m));

  return {
    slug: event.slug,
    market_event_id: String(event.event_id || event.id),
    status_label: formatEventStatusLabel(event),
    volume: formatVolume(event.volume_24hr ? Number(event.volume_24hr) : null),
    draw_probability: formatProbability(
      drawMarket?.outcome_yes_price ? Number(drawMarket.outcome_yes_price) : null,
    ),
    home_team: toMatchTeam(homeMarket, 'text-[#12B3CF]'),
    away_team: toMatchTeam(awayMarket, 'text-[#F5BA80]'),
  };
}

function toMatchTeam(market: MarketListMarket | undefined, probabilityClassName: string) {
  const name = market?.group_item_title || '--';
  return {
    name,
    probability: formatProbability(
      market?.outcome_yes_price ? Number(market.outcome_yes_price) : null,
    ),
    probability_class_name: probabilityClassName,
    // 队徽按队名查静态表（大小写不敏感、含别名），查不到回落到市场图标
    image_src: getTeamLogo(name) || market?.market_icon || market?.market_image || undefined,
    avatar_fallback: getInitials(name),
  };
}

/** MarketListEvent 没有 game_start_time，用 end_date 近似比赛时间做状态展示 */
function formatEventStatusLabel(event: MarketListEvent) {
  if (event.closed) return 'Closed';
  const endTime = dayjs(event.end_date ?? '');
  if (!endTime.isValid()) return '';
  if (endTime.isBefore(dayjs()) && event.active) return 'Live';

  return formatDateTime(endTime.valueOf(), 'MMM D, h:mm A');
}

function formatStatusLabel(game: WorldCupGameItem) {
  const startTime = dayjs(game.game_start_time);
  if (!startTime.isValid()) return '';

  if (game.closed) return 'Closed';
  if (startTime.isBefore(dayjs()) && game.active) return 'Live';

  return formatDateTime(startTime.valueOf(), 'MMM D, h:mm A');
}

function findTeamOdds(game: WorldCupGameItem, teamName: string) {
  // Bosnia - Herzegovina;
  return game.moneyline.find((odd) => {
    if (odd.group_item_title === 'Bosnia and Herzegovina') {
      return 'bosnia-herzegovina' === teamName.toLowerCase();
    }
    return odd.group_item_title.toLowerCase() === teamName.toLowerCase();
  });
}

function formatProbability(price?: number | null) {
  if (price == null) return '--';

  return `${(price * 100).toFixed(1)}%`;
}

function formatVolume(volume?: number | null) {
  if (volume == null) return '$0';
  if (volume >= 1_000_000) return `$${(volume / 1_000_000).toFixed(2)}M`;
  if (volume >= 1_000) return `$${(volume / 1_000).toFixed(2)}K`;

  return `$${volume.toFixed(0)}`;
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}
